import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Browser } from '@capacitor/browser';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Download, ExternalLink, FileText } from 'lucide-react';
import { downloadDocumentBlob } from '@features/documents/api';
import { fetchIssueDownloadUrl } from '@features/news-stand/api';
import { toApiError } from '@shared/api/errors';
import { Button } from '@shared/ui/primitives/Button';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toast } from '@shared/ui/feedback/Toast';
import { HeroHeader, HeroHeaderActionButton } from '@shared/ui/headers/HeroHeader';
import { platform } from '@shared/lib/platform';

interface PdfViewerLocationState {
  pdfUrl?: string;
  title?: string;
  fileName?: string;
}

export function PdfViewerPage(): JSX.Element {
  const { newsstandId, articleId, documentId } = useParams<{
    newsstandId: string;
    articleId: string;
    documentId: string;
  }>();
  const location = useLocation();
  const [params] = useSearchParams();
  const [downloading, setDownloading] = useState(false);
  const state = (location.state as PdfViewerLocationState | null) ?? null;
  const isDocumentViewer = !!documentId;
  const title = state?.title ?? params.get('title') ?? 'Article PDF';
  const fileName = state?.fileName ?? params.get('file') ?? `${title}.pdf`;
  const [documentObjectUrl, setDocumentObjectUrl] = useState<string | null>(null);

  const urlQuery = useQuery({
    queryKey: ['news-article-pdf', newsstandId, articleId],
    queryFn: () => fetchIssueDownloadUrl(newsstandId ?? '', articleId ?? ''),
    enabled: !isDocumentViewer && !state?.pdfUrl && !!newsstandId && !!articleId,
    retry: false,
  });

  const documentBlobQuery = useQuery({
    queryKey: ['document-pdf', documentId],
    queryFn: () => downloadDocumentBlob(documentId ?? ''),
    enabled: isDocumentViewer && !!documentId,
    retry: false,
  });

  useEffect(() => {
    if (!documentBlobQuery.data) return undefined;

    const objectUrl = URL.createObjectURL(documentBlobQuery.data);
    setDocumentObjectUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
      setDocumentObjectUrl((current) => (current === objectUrl ? null : current));
    };
  }, [documentBlobQuery.data]);

  const pdfUrl = state?.pdfUrl ?? urlQuery.data ?? documentObjectUrl;
  const backTo = isDocumentViewer
    ? '/app/documents'
    : (newsstandId ? `/app/news-stands/${newsstandId}` : '/app/news-stands');
  const embeddedUrl = useMemo(() => {
    if (!pdfUrl) return null;
    return `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`;
  }, [pdfUrl]);

  const openExternal = async (): Promise<void> => {
    if (!pdfUrl) return;
    if (platform.isNative()) {
      if (isDocumentViewer && documentBlobQuery.data) {
        const uri = await writeBlobToCache(documentBlobQuery.data, fileName, 'documents');
        await Browser.open({ url: uri, presentationStyle: 'popover' });
        return;
      }
      await Browser.open({ url: pdfUrl, presentationStyle: 'popover' });
      return;
    }

    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = async (): Promise<void> => {
    if (!pdfUrl || downloading) return;
    setDownloading(true);
    try {
      const blob = isDocumentViewer && documentId
        ? await downloadDocumentBlob(documentId)
        : await fetchPdfBlob(pdfUrl);
      await saveBlobAsDownload(blob, fileName, title);
      toast.success('Download started', title);
    } catch (error) {
      const apiError = toApiError(error);
      toast.danger("Couldn't download PDF", apiError.message);
    } finally {
      setDownloading(false);
    }
  };

  if (!isDocumentViewer && (!newsstandId || !articleId)) {
    return (
      <div className="min-h-full bg-paper">
        <HeroHeader title="Article PDF" subtitle="Preview" backTo="/app/news-stands" backBehavior="parent" />
        <div className="px-5 pt-4 pb-safe-or-6">
          <EmptyState
            icon={<FileText className="h-7 w-7" strokeWidth={1.5} />}
            title="Nothing to preview."
            description="Open this screen from a news article."
          />
        </div>
      </div>
    );
  }

  if ((isDocumentViewer ? documentBlobQuery.isLoading : urlQuery.isLoading) && !pdfUrl) {
    return (
      <div className="min-h-full bg-paper">
        <HeroHeader title={title} subtitle="Preparing preview" backTo={backTo} backBehavior="parent" />
        <div className="px-5 pt-4 pb-safe-or-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="mt-4 h-[70vh] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if ((isDocumentViewer ? documentBlobQuery.isError : urlQuery.isError) && !pdfUrl) {
    const apiError = toApiError(isDocumentViewer ? documentBlobQuery.error : urlQuery.error);
    const unavailable = apiError.status === 404;

    return (
      <div className="min-h-full bg-paper">
        <HeroHeader title={title} subtitle="Preview unavailable" backTo={backTo} backBehavior="parent" />
        <div className="px-5 pt-4 pb-safe-or-6">
          {unavailable ? (
            <EmptyState
              icon={<FileText className="h-7 w-7" strokeWidth={1.5} />}
              title={isDocumentViewer ? 'Document unavailable.' : 'Article unavailable.'}
              description={
                isDocumentViewer
                  ? 'This PDF is no longer available in documents.'
                  : 'This PDF is no longer available in the news stand.'
              }
            />
          ) : (
            <ErrorState onRetry={() => void (isDocumentViewer ? documentBlobQuery.refetch() : urlQuery.refetch())} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        title={title}
        subtitle="Preview in app"
        backTo={backTo}
        backBehavior="parent"
        action={
          pdfUrl ? (
            <HeroHeaderActionButton
              icon={<Download className="h-4 w-4" strokeWidth={1.75} />}
              label="Download PDF"
              onClick={() => void handleDownload()}
              disabled={downloading}
              loading={downloading}
            />
          ) : undefined
        }
      />

      <div className="px-5 pt-4 pb-safe-or-6">
        <div className="mb-3 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => void openExternal()}>
            <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
            Open externally
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleDownload()}
            disabled={downloading}
            loading={downloading}
          >
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Download
          </Button>
        </div>

        <div className="overflow-hidden rounded-xl border border-black/5 bg-white shadow-sm">
          {embeddedUrl ? (
            <iframe
              title={title}
              src={embeddedUrl}
              className="h-[calc(100dvh-15rem)] min-h-[65vh] w-full border-0 bg-white"
              loading="eager"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          ) : (
            <div className="flex min-h-[65vh] items-center justify-center p-6">
              <EmptyState
                icon={<FileText className="h-7 w-7" strokeWidth={1.5} />}
                title="Preview unavailable."
                description="Open the PDF externally or download it instead."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export async function saveBlobAsDownload(blob: Blob, filename: string, title: string): Promise<void> {
  const safeName = sanitizeFilename(filename);
  if (platform.isNative()) {
    const uri = await writeBlobToCache(blob, safeName, 'pdfs');
    await Share.share({ title, url: uri, dialogTitle: 'Save PDF' });
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = safeName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

async function fetchPdfBlob(pdfUrl: string): Promise<Blob> {
  const response = await fetch(pdfUrl);
  if (!response.ok) throw new Error('Download failed. Please try again.');
  return response.blob();
}

async function writeBlobToCache(blob: Blob, filename: string, folder: string): Promise<string> {
  const base64 = await blobToBase64(blob);
  const path = `${folder}/${sanitizeFilename(filename)}`;
  await Filesystem.writeFile({
    path,
    data: base64,
    directory: Directory.Cache,
    recursive: true,
  });
  const { uri } = await Filesystem.getUri({ directory: Directory.Cache, path });
  return uri;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(blob);
  });
}

function sanitizeFilename(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return cleaned || 'article.pdf';
}
